import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import { FunctionalComponent } from 'vue';
import { MaybeRef } from '@vueuse/core';

import { BotIcon, CableIcon, ChartColumnIcon, ChartColumnStackedIcon, Code, CreditCardIcon, DollarSignIcon, HouseIcon, LayoutDashboardIcon, LayoutPanelTopIcon, MegaphoneIcon, MousePointerClickIcon, ScrollTextIcon, Settings, SettingsIcon, ShieldCheck, ShieldMinusIcon, SquareTerminalIcon, TestTubeDiagonalIcon, TicketsIcon, ToyBrickIcon, UserIcon, UsersIcon, UserStarIcon } from "lucide-vue-next"
import LambdaIcon from '@/components/Atomic/Miscellaneous/LambdaIcon.vue';

interface ILink {
    name: string;
    href: string;
    icon: FunctionalComponent|typeof LambdaIcon;
    count?: MaybeRef<number>;
    id: string;
    tooltip?: string;
    new_feature_visited?: boolean;
    children?: IChildLink[];
}

interface IChildLink {
    name: string;
    href: string;
    id: string;
    tooltip?: string;
    new_feature_visited?: boolean;
}

const { isPluginSubscribed } = useSubscriptions();
const { membersCount } = useTeamsAndMembers();
const { featureVisited } = useNewFeatures();
const { currentFolder } = useFolders();

export default () => {
    const page = computed(() => usePage<ITopolPageProps>().url);
    const isPersonalTeam = computed(
        () => usePage<ITopolPageProps>().props.team.personal_team
    );
    const { subscriptionRouteName } = useSubscriptionLabel();
    const globalProps = computed(() => usePage<ITopolPageProps>().props);

    const linksApp = computed((): ILink[] => {
        const links: ILink[] = [
            {
                name: 'Templates',
                href: route('templates.index', {
                    template_folder_id: currentFolder.value?.id,
                }),
                icon: HouseIcon,
                count: undefined,
                id: '/templates',
                children: [
                    {
                        name: 'My Templates',
                        href: route('templates.index', {
                            template_folder_id: currentFolder.value?.id,
                        }),
                        id: '/templates',
                    },
                ]
            },
            {
                name: 'Usage statistics',
                href: route('usage-statistics'),
                icon: ChartColumnIcon,
                count: undefined,
                id: '/usage-statistics',
            },
            {
                name: 'Integrations',
                href: route('integrations.index'),
                icon: CableIcon,
                count: undefined,
                id: '/integrations',
                children: [
                    {
                        name: 'Active',
                        href: route('integrations.index'),
                        id: '/integrations',
                    },
                    {
                        name: 'Available',
                        href: route('integrations.available.index'),
                        id: '/integrations/available',
                    },
                ]
            },
        ];
        if (isPersonalTeam.value) {
            links.splice(1, 0, {
                name: 'Plugin',
                href: route('plugin.api-tokens.index'),
                icon: Code,
                count: undefined,
                id: '/plugin',
            });
        }
        if (!isPersonalTeam.value || isPluginSubscribed.value) {
            links.splice(1, 0, {
                name: 'Members',
                href: route('members.index'),
                icon: UsersIcon,
                count: membersCount.value,
                reserveBadge: true,
                id: '/members',
            });
        }
            if (
                globalProps.value.team_role.key === 'owner' ||
                globalProps.value.team_role.key === 'admin' ||
                globalProps.value.team_role.key === 'editor'
            ) {

            links.splice(1, 0, {
                name: 'Editor settings',
                href: route('editor-preferences.appearance.index'),
                icon: MousePointerClickIcon,
                count: undefined,
                id: '/editor-preferences',
                new_feature_visited: !featureVisited('product_feeds'),
            });

            links.splice(6, 0, {
                name:
                    isPersonalTeam.value === true
                        ? 'Settings'
                        : 'Team Settings',
                href:
                    isPersonalTeam.value === true
                        ? route('settings.profile-settings.index')
                        : route('settings.team-settings.index'),
                icon: SettingsIcon,
                count: undefined,
                id: '/settings',
            });
        }
        if (globalProps.value.team_role.key === 'owner') {
            links.splice(7, 0, {
                name: 'Billing',
                href: route(subscriptionRouteName.value),
                icon: CreditCardIcon,
                count: undefined,
                id: '/billing/',
            });
        }

        if (globalProps.value.isAdmin) {
            links.push({
                name: 'Kiosk',
                href: route('kiosk.dashboard.index'),
                icon: ShieldCheck,
                count: undefined,
                id: '/kiosk',
            });
        }

        return links;
    });

    const linksKiosk = computed((): ILink[] => {
        return [
            {
                name: 'Dashboard',
                href: route('kiosk.dashboard.index'),
                icon: LayoutDashboardIcon,
                id: '/kiosk/dashboard',
            },
            {
                name: 'Users',
                href: route('kiosk.users.index'),
                icon: UserIcon,
                id: '/kiosk/users',
            },
            {
                name: 'Teams',
                href: route('kiosk.teams.index'),
                icon: UsersIcon,
                id: '/kiosk/teams',
            },
            {
                name: 'Admins',
                href: route('kiosk.admins.index'),
                icon: UserStarIcon,
                id: '/kiosk/admins',
            },
            {
                name: 'Subscriptions',
                href: route('kiosk.subscriptions.index'),
                icon: TicketsIcon,
                id: '/kiosk/subscriptions',
            },
            {
                name: 'Announcements',
                href: route('kiosk.announcements.index'),
                icon: MegaphoneIcon,
                id: '/kiosk/announcements',
            },
            {
                name: 'Api Tokens',
                href: route('kiosk.api-tokens.index'),
                icon: ToyBrickIcon,
                id: '/kiosk/tokens',
            },
            {
                name: 'Open AI Stats',
                href: route('kiosk.open-ai.index'),
                icon: SquareTerminalIcon,
                id: '/kiosk/open-ai',
            },
            {
                name: 'Premade templates',
                href: route('kiosk.premade-templates.index'),
                icon: LayoutPanelTopIcon,
                id: '/kiosk/premade-templates',
            },
            {
                name: 'Metrics',
                href: route('kiosk.metrics.api-tokens.index'),
                icon: ChartColumnStackedIcon,
                id: '/kiosk/metrics',
            },
            {
                name: 'Blocked resources',
                href: route('kiosk.blocked-domains.index'),
                icon: ShieldMinusIcon,
                id: '/kiosk/blocked-resources',
            },
            {
                name: 'Invoices',
                href: route('kiosk.invoices.index'),
                icon: ScrollTextIcon,
                id: '/kiosk/invoices',
            },
            {
                name: 'Payment requests',
                href: route('kiosk.payment-requests.index'),
                icon: DollarSignIcon,
                id: '/kiosk/payment-requests',
            },
            {
                name: 'Testing logs',
                href: route('kiosk.testing-logs.index'),
                icon: TestTubeDiagonalIcon,
                id: '/kiosk/testing-logs',
            },
            {
                name: 'Lambdas',
                href: route('kiosk.lambdas.index'),
                icon: LambdaIcon,
                id: '/kiosk/lambdas',
            },
        ];
    });

    const links = computed(() => {
        return page.value.startsWith('/kiosk')
            ? linksKiosk.value
            : linksApp.value;
    });

    return {
        links,
    };
};
